'use client'

/**
 * Page Controller Framework — client hook.
 *
 * Subscribes to the per-(workspace, page) broadcast channel and feeds
 * incoming messages through the supplied reducer. Returns the current
 * reduced state plus a `dispatch(action, payload)` helper that:
 *
 *   1. Broadcasts the action so peer tabs and chat see it.
 *   2. Optimistically applies it locally so the dispatching tab doesn't
 *      have to wait for the round-trip.
 *
 * Dedupe: the local dispatch records the generated `messageId`; when the
 * same id loops back over the channel we drop it. (We also tell Supabase
 * Realtime not to echo our own broadcasts with `self: false`, but we
 * keep the in-memory set as defense in depth — different driver versions
 * have historically been inconsistent about `self`.)
 *
 * Lifecycle: a single channel is opened on mount and torn down on
 * unmount. Page state from before the subscription succeeded is
 * preserved; we don't try to backfill missed messages because broadcast
 * has no replay.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createClient } from '@/utils/supabase/client'
import { useCurrentWorkspace } from '@/hooks/useCurrentWorkspace'
import {
  pageControllerChannelName,
  type PageControllerMessage,
} from './types'

export type { PageControllerMessage, PageControllerSource } from './types'

export type PageControllerReducer<TState> = (
  state: TState,
  msg: PageControllerMessage,
) => TState

export interface UsePageControllerOptions {
  /** Override the workspace id. Defaults to the current workspace from context. */
  workspaceId?: string | null
  /**
   * When true (default), local `dispatch` also broadcasts so other tabs
   * + chat see the action. Set false for "private" UI actions that
   * shouldn't propagate.
   */
  broadcastLocal?: boolean
}

type DispatchFn = (
  action: string,
  payload?: Record<string, unknown>,
) => void

/**
 * Generate a reasonably-unique message id without depending on Node's
 * `crypto.randomUUID()` (which is browser-only above HTTPS in some old
 * environments). Collisions across tabs over the same minute are
 * statistically negligible for our dedupe purposes.
 */
function makeMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function usePageController<TState>(
  page: string,
  initial: TState,
  reducer: PageControllerReducer<TState>,
  options: UsePageControllerOptions = {},
): [TState, DispatchFn] {
  const { workspace } = useCurrentWorkspace()
  const workspaceId = options.workspaceId ?? workspace?.id ?? null
  const broadcastLocal = options.broadcastLocal !== false

  const [state, setState] = useState<TState>(initial)

  // Track ids we've already applied locally so the broadcast echo (if
  // any) is dropped. Bounded to the last 64 dispatches to cap memory.
  const seenIdsRef = useRef<Set<string>>(new Set())
  const seenOrderRef = useRef<string[]>([])

  // Keep the latest reducer in a ref so the channel subscription doesn't
  // need to be rebuilt on every render.
  const reducerRef = useRef(reducer)
  useEffect(() => {
    reducerRef.current = reducer
  }, [reducer])

  // Channel ref so dispatch() can send through it without rebuilding.
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(
    null,
  )

  const rememberId = useCallback((id: string) => {
    seenIdsRef.current.add(id)
    seenOrderRef.current.push(id)
    if (seenOrderRef.current.length > 64) {
      const drop = seenOrderRef.current.shift()
      if (drop) seenIdsRef.current.delete(drop)
    }
  }, [])

  // ── Subscribe to the broadcast channel ────────────────────────────────
  useEffect(() => {
    if (!workspaceId) return

    const supabase = createClient()
    const channel = supabase
      .channel(pageControllerChannelName(workspaceId, page), {
        config: { broadcast: { self: false, ack: false } },
      })
      .on('broadcast', { event: 'page-controller' }, ({ payload }) => {
        const msg = payload as PageControllerMessage | undefined
        if (!msg || msg.page !== page) return
        if (seenIdsRef.current.has(msg.messageId)) return
        rememberId(msg.messageId)
        setState((prev) => {
          try {
            return reducerRef.current(prev, msg)
          } catch (err) {
            console.error('[page-controller] reducer threw:', err)
            return prev
          }
        })
      })
      .subscribe()

    channelRef.current = channel

    return () => {
      channelRef.current = null
      void supabase.removeChannel(channel)
    }
  }, [workspaceId, page, rememberId])

  // ── Local dispatch — apply immediately + (optionally) broadcast ───────
  const dispatch = useCallback<DispatchFn>(
    (action, payload) => {
      const msg: PageControllerMessage = {
        page,
        action,
        payload: payload ?? {},
        messageId: makeMessageId(),
        source: 'user',
        ts: Date.now(),
      }
      rememberId(msg.messageId)
      setState((prev) => {
        try {
          return reducerRef.current(prev, msg)
        } catch (err) {
          console.error('[page-controller] local reducer threw:', err)
          return prev
        }
      })

      if (broadcastLocal && channelRef.current) {
        const ch = channelRef.current
        void ch.send({
          type: 'broadcast',
          event: 'page-controller',
          payload: msg,
        })
      }
    },
    [page, broadcastLocal, rememberId],
  )

  return useMemo(() => [state, dispatch], [state, dispatch])
}
